import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CircleNotch, MagnifyingGlass, CaretRight, ClipboardText } from "@phosphor-icons/react";

export const STATUS_META = {
  received: { label: "Received from Maximo", cls: "bg-slate-100 text-slate-700" },
  mapped: { label: "Mapped", cls: "bg-slate-100 text-slate-700" },
  pushed_to_aroflo: { label: "Pushed to AroFlo", cls: "bg-[#0055FF]/10 text-[#0055FF]" },
  allocated: { label: "Allocated", cls: "bg-sky-100 text-sky-700" },
  in_progress: { label: "In progress", cls: "bg-[#D97706]/10 text-[#D97706]" },
  awaiting_quote: { label: "Awaiting quote", cls: "bg-orange-100 text-orange-700" },
  completed: { label: "Completed in AroFlo", cls: "bg-[#059669]/10 text-[#059669]" },
  reported_to_maximo: { label: "Reported to Maximo", cls: "bg-teal-100 text-teal-700" },
  closed: { label: "Closed", cls: "bg-neutral-800 text-white" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

export default function WorkOrdersDashboard() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/maximo/workorders").then((r) => setData(r.data)).catch(() => setData({ workorders: [], counts: {} })); }, []);

  if (!data) return <div className="mt-6 flex h-48 items-center justify-center"><CircleNotch size={26} className="animate-spin text-[#0055FF]" /></div>;

  const rows = data.workorders.filter((w) => {
    if (filter !== "all" && w.status !== filter) return false;
    if (q && !`${w.wonum} ${w.description_long} ${w.location_long} ${w.allocated_to || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="mt-6 space-y-6" data-testid="maximo-workorders-dashboard">
      <div className="flex items-center gap-2 rounded-sm border border-[#D97706]/30 bg-[#D97706]/5 px-4 py-2.5">
        <ClipboardText size={18} weight="fill" className="text-[#D97706]" />
        <span className="text-sm text-neutral-700"><span className="font-semibold">Illustrative data.</span> Work orders below are mock records that mirror the GBELEC reactive-maintenance flow for audit demonstration.</span>
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="maximo-wo-status-cards">
        <button onClick={() => setFilter("all")} data-testid="wo-filter-all"
          className={`rounded-sm border p-3 text-left transition-all ${filter === "all" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-neutral-200 bg-white hover:border-neutral-300"}`}>
          <p className="text-2xl font-black tracking-tighter">{data.total}</p>
          <p className="text-xs font-semibold uppercase tracking-wider opacity-80">All work orders</p>
        </button>
        {data.status_flow.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`wo-filter-${s}`}
            className={`rounded-sm border p-3 text-left transition-all ${filter === s ? "border-[#0055FF] ring-1 ring-[#0055FF]" : "border-neutral-200 hover:border-neutral-300"} bg-white`}>
            <p className="text-2xl font-black tracking-tighter text-[#0A0A0A]">{data.counts[s] || 0}</p>
            <p className="truncate text-xs font-semibold uppercase tracking-wider text-neutral-500">{STATUS_META[s]?.label || s}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input data-testid="wo-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search WO #, description, location…"
          className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]" />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="maximo-wo-table">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                {["WO #", "Description", "Location", "Priority", "AroFlo task", "Allocated to", "Status", "Last sync", ""].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => {
                const m = STATUS_META[w.status] || { label: w.status, cls: "bg-neutral-100" };
                return (
                  <tr key={w.wonum} className="group border-b border-neutral-100 last:border-0 hover:bg-neutral-50" data-testid={`maximo-wo-row-${w.wonum}`}>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-[#0055FF]">{w.wonum}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-neutral-700" title={w.description_long}>{w.description_long}</td>
                    <td className="px-4 py-3 text-neutral-600">{w.location_short}</td>
                    <td className="px-4 py-3"><span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold">{w.priority}</span></td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-600">{w.aroflo_task_id || "—"}</td>
                    <td className="px-4 py-3 text-neutral-600">{w.allocated_to || "—"}</td>
                    <td className="px-4 py-3"><span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${m.cls}`}>{m.label}</span></td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{fmt(w.last_sync_at)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/maximo/work-orders/${w.wonum}`} data-testid={`maximo-wo-view-${w.wonum}`}
                        className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs font-semibold text-[#0055FF] hover:bg-[#0055FF]/5">
                        Audit <CaretRight size={13} weight="bold" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-neutral-400">No work orders match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
